/**
 * Lets a list that a subscription keeps live take a full re-read without
 * losing newer changes. The re-read is a snapshot taken while subscription
 * updates keep arriving, so an entry changed locally after the re-read began
 * wins over it, and a re-read that began before one already applied (or
 * before a full load) is dropped.
 */
export default class LiveListSyncGuard {
  private sequence = 0
  private latestAppliedSync = 0
  private readonly changedAt = new Map<string, number>()

  /** Call before sending a re-read; hand the token back with its reply. */
  public beginSync(): number {
    return ++this.sequence
  }

  /** A subscription update, save reply or local edit touched this entry. */
  public markChanged(id: string) {
    this.changedAt.set(id, ++this.sequence)
  }

  /** The list was cleared or fully reloaded: re-reads in flight no longer apply. */
  public reset() {
    this.changedAt.clear()
    this.latestAppliedSync = ++this.sequence
  }

  /** False for a stale re-read. Otherwise records it as the latest applied. */
  public tryApply(syncToken: number): boolean {
    if (syncToken < this.latestAppliedSync) {
      return false
    }

    this.latestAppliedSync = syncToken
    // Changes older than this re-read can't outrank it or any later one.
    this.changedAt.forEach((changedAt, id) => {
      if (changedAt <= syncToken) {
        this.changedAt.delete(id)
      }
    })

    return true
  }

  /** Whether the entry changed locally after the re-read began. */
  public isChangedSince(id: string, syncToken: number): boolean {
    return (this.changedAt.get(id) ?? 0) > syncToken
  }
}
